import React, { useState } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { ChevronRight } from "lucide-react";

/** Per-viewer memory of which artifacts the user opened, so a card they
 *  expanded stays open on reload. Cards start collapsed: a long review or a
 *  500-row table opened in full buried the chat answer under it (22516-3
 *  live). Browser storage can be missing or throw (private window, blocked
 *  site data); the card then just starts collapsed. */
const STORAGE_KEY = "artie.expandedArtifacts";

function readExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids.map(String) : []);
  } catch {
    return new Set();
  }
}

function writeExpanded(id: string, expanded: boolean) {
  try {
    const ids = readExpanded();
    if (expanded) ids.add(id);
    else ids.delete(id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // Convenience only: a failed write keeps the in-memory state.
  }
}

const KIND_LABEL: Record<string, string> = {
  file: "File",
  table: "Table",
  document: "Document",
  code: "Code",
  html: "Page",
};

/** Wraps an artifact card with a one-line header that folds the card away.
 *  Collapsed (the default) shows only the header, with the title, so a long
 *  review or a 500-row table takes one line of the transcript until opened;
 *  expanded shows a small chevron and the kind above the card. With
 *  `exportOnly` it is a fixed header (title, kind, actions) and never opens. */
export function CollapsibleArtifact({
  artifactId,
  title,
  kind,
  actions,
  exportOnly = false,
  size,
  status,
  preview,
  children,
}: {
  artifactId: string;
  title: string | null | undefined;
  kind: string | null | undefined;
  /** Shown on the right of the header while collapsed (e.g. Export), so an
   *  artifact can be downloaded without opening it. */
  actions?: React.ReactNode;
  /** A download, not something to read in the chat: the header and its
   *  actions only, with no toggle and no inline body (ELASV1-3518, the
   *  invoice-review files). */
  exportOnly?: boolean;
  /** An export-only artifact's body length, shown next to its title. */
  size?: number;
  /** The artifact's status; an export-only card has no body to show it, so
   *  its header says when the file is still being made or failed. */
  status?: "streaming" | "complete" | "failed";
  /** Shown under an export-only header: a fixed glance at the download (the
   *  issues table's first rows), with nothing to open. */
  preview?: React.ReactNode;
  children: React.ReactNode;
}) {
  if (exportOnly) {
    return (
      <>
        <ExportOnlyHeader
          title={title}
          kind={kind}
          actions={actions}
          size={size}
          status={status}
        />
        {preview}
      </>
    );
  }
  return (
    <FoldingArtifact artifactId={artifactId} title={title} kind={kind} actions={actions}>
      {children}
    </FoldingArtifact>
  );
}

const STATUS_TEXT: Record<string, string> = {
  streaming: "Preparing…",
  failed: "Failed",
};

/** "1.8 MB". The backend's size counts characters, not bytes; a review is
 *  near-ASCII markdown/JSON, so the two agree closely enough for a hint. */
function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function ExportOnlyHeader({
  title,
  kind,
  actions,
  size,
  status,
}: {
  title: string | null | undefined;
  kind: string | null | undefined;
  actions?: React.ReactNode;
  size?: number;
  status?: string;
}) {
  const statusText = STATUS_TEXT[status ?? ""];
  return (
    <Box sx={{ my: 1, display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1, py: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
          {title || "Artifact"}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {KIND_LABEL[kind ?? ""] ?? "Artifact"}
        </Typography>
        {typeof size === "number" && size > 0 && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {formatSize(size)}
          </Typography>
        )}
        {statusText && (
          <Typography
            variant="caption"
            sx={{ color: status === "failed" ? "error.main" : "text.secondary" }}
          >
            {statusText}
          </Typography>
        )}
      </Box>
      {actions && <Box sx={{ ml: "auto" }}>{actions}</Box>}
    </Box>
  );
}

function FoldingArtifact({
  artifactId,
  title,
  kind,
  actions,
  children,
}: {
  artifactId: string;
  title: string | null | undefined;
  kind: string | null | undefined;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(() => readExpanded().has(artifactId));
  const toggle = () => {
    writeExpanded(artifactId, !open);
    setOpen(!open);
  };
  const label = title || "Artifact";
  return (
    <Box sx={{ my: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ButtonBase
          onClick={toggle}
          aria-expanded={open}
          aria-label={`${open ? "Collapse" : "Expand"} ${label}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            color: "text.secondary",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              transition: "transform 150ms",
              transform: open ? "rotate(90deg)" : "none",
            }}
          >
            <ChevronRight size={15} />
          </Box>
          {/* Expanded, the card shows its own title; the header names it only
              once the card is folded away, so the title never appears twice. */}
          {!open && (
            <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
              {label}
            </Typography>
          )}
          <Typography variant="caption">{KIND_LABEL[kind ?? ""] ?? "Artifact"}</Typography>
        </ButtonBase>
        {/* A sibling of the toggle, not a child: a button inside a button is
            invalid, and a click on Export must not fold or unfold the card. */}
        {!open && actions && <Box sx={{ ml: "auto" }}>{actions}</Box>}
      </Box>
      {open && children}
    </Box>
  );
}
